import { useRef, useState } from "react";
import api, { API_BASE, formatError } from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileSpreadsheet, Check, X, Download } from "lucide-react";
import { toast } from "sonner";

export default function UploadDialog({ open, onOpenChange }) {
  const { refreshMeta } = useFilters();
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [mode, setMode] = useState("append");
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setSheets([]); setSelectedSheet(""); setMode("append"); };

  const doPreview = async (f) => {
    setBusy(true);
    try {
      // First: inspect sheets
      const fd0 = new FormData(); fd0.append("file", f);
      const insp = await api.post("/exports/inspect", fd0).catch(() => null);
      if (insp?.data?.sheets) {
        setSheets(insp.data.sheets);
        const first = insp.data.sheets.find(s => s.header_row) || insp.data.sheets[0];
        if (first) setSelectedSheet(first.name);
      }
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/failures/upload/preview", fd);
      setPreview(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Preview failed"); setPreview(null); }
    finally { setBusy(false); }
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) { toast.error("Only .xlsx / .xls files supported"); return; }
    setFile(f);
    doPreview(f);
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("mode", mode);
      if (selectedSheet) fd.append("sheet_name", selectedSheet);
      const { data } = await api.post("/failures/upload", fd);
      toast.success(`Imported ${data.inserted} records (parsed ${data.parsed})`);
      refreshMeta();
      reset();
      onOpenChange(false);
      window.dispatchEvent(new Event("trams:refresh"));
      setTimeout(() => window.location.reload(), 400);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem("trams_token");
    fetch(`${API_BASE}/exports/template`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "LAPORAN_GANGGUAN_template.xlsx"; a.click(); URL.revokeObjectURL(url);
      });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-2xl" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1e40af] font-black">Upload Excel Database</DialogTitle>
          <DialogDescription>Import LAPORAN GANGGUAN sheet. Header auto-detected from cell "No".</DialogDescription>
        </DialogHeader>

        {!file && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${dragOver ? "border-[#1e40af] bg-blue-50" : "border-slate-300 hover:border-[#1e40af] hover:bg-slate-50"}`}
            data-testid="upload-dropzone"
          >
            <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleFile(e.target.files[0])} data-testid="upload-input" />
            <Upload className="w-10 h-10 mx-auto text-[#1e40af] mb-3" />
            <div className="font-semibold text-slate-900">Drop Excel file here or click to select</div>
            <div className="text-xs text-slate-500 mt-1">Supports .xlsx and .xls · Sheet: LAPORAN GANGGUAN</div>
            <button onClick={(e) => { e.stopPropagation(); downloadTemplate(); }} className="mt-4 text-xs text-[#1e40af] font-semibold inline-flex items-center hover:underline" data-testid="upload-template">
              <Download className="w-3.5 h-3.5 mr-1" /> Download template
            </button>
          </div>
        )}

        {file && preview && (
          <div className="space-y-4" data-testid="upload-preview">
            <div className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-8 h-8 text-[#1e40af]" />
                <div>
                  <div className="font-semibold text-sm text-slate-900">{file.name}</div>
                  <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
                </div>
              </div>
              <button className="text-slate-400 hover:text-red-500 p-2" onClick={reset}><X className="w-4 h-4" /></button>
            </div>

            <div className="grid grid-cols-3 gap-3 text-xs">
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="text-[10px] uppercase text-slate-500 font-semibold">Rows Detected</div>
                <div className="text-xl font-black text-slate-900 mt-1">{preview.row_count.toLocaleString()}</div>
              </div>
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="text-[10px] uppercase text-slate-500 font-semibold">Date Range</div>
                <div className="text-xs font-semibold text-slate-900 mt-1">{preview.date_range[0] || "—"}<br/>→ {preview.date_range[1] || "—"}</div>
              </div>
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="text-[10px] uppercase text-slate-500 font-semibold">Parse Errors</div>
                <div className="text-xl font-black text-slate-900 mt-1">{preview.errors.length}</div>
              </div>
            </div>

            <div>
              <div className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Preview (first 10 rows)</div>
              <div className="border border-slate-200 rounded-lg overflow-x-auto trams-scroll max-h-56">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-700 sticky top-0">
                    <tr>
                      {["Date","Time","Train","Equipment","Kejadian","Delay(s)","Status"].map(h => <th key={h} className="text-left px-3 py-2 font-semibold">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((r) => {
                      const key = r.id ?? `${r.tanggal || ""}-${r.waktu || ""}-${r.no_ts || ""}-${(r.kejadian || "").slice(0, 20)}`;
                      return (
                      <tr key={key} className="border-t border-slate-100">
                        <td className="px-3 py-1.5">{r.tanggal}</td>
                        <td className="px-3 py-1.5">{r.waktu || "—"}</td>
                        <td className="px-3 py-1.5">{r.no_ts || "—"}</td>
                        <td className="px-3 py-1.5">{r.equipment || "—"}</td>
                        <td className="px-3 py-1.5 max-w-[180px] truncate" title={r.kejadian}>{r.kejadian || "—"}</td>
                        <td className="px-3 py-1.5">{r.delay_seconds || 0}</td>
                        <td className="px-3 py-1.5">{r.status}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Sheet & Column Mapping</div>
              {sheets.length > 0 ? (
                <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Pilih Sheet</div>
                    <Select value={selectedSheet} onValueChange={setSelectedSheet}>
                      <SelectTrigger data-testid="upload-sheet"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {sheets.map(s => (
                          <SelectItem key={s.name} value={s.name}>
                            {s.name} {s.header_row ? `(header row ${s.header_row}, ${s.row_count} rows)` : "(no header detected)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedSheet && sheets.find(s => s.name === selectedSheet)?.auto_mapping && (
                    <div className="text-[11px] text-slate-600 mt-2">
                      <div className="font-semibold text-slate-700 mb-1">Auto-detected mapping:</div>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(sheets.find(s => s.name === selectedSheet).auto_mapping).slice(0, 12).map(([k, v]) => (
                          <span key={k} className="px-2 py-0.5 bg-white rounded border border-slate-200 text-[10px]">
                            <b>{k}</b> → {v}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-slate-500 italic">Auto-detected sheet will be used.</div>
              )}
            </div>

            <div>
              <div className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Import Mode</div>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="upload-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="append">Append (add new records, dedup by All Failure Code)</SelectItem>
                  <SelectItem value="replace">Replace (delete existing then import)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={reset}>Cancel</Button>
              <Button className="bg-[#1e40af] hover:bg-[#1e3a8a]" disabled={busy} onClick={submit} data-testid="upload-submit">
                {busy ? "Importing…" : <><Check className="w-3.5 h-3.5 mr-1.5" /> Import {preview.row_count} rows</>}
              </Button>
            </div>
          </div>
        )}

        {busy && !preview && <div className="text-xs text-slate-500 text-center">Parsing file…</div>}
      </DialogContent>
    </Dialog>
  );
}
